import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import axios from 'axios';

interface TeamMember {
  name: string;
  email: string;
  capacity: number;
  role: 'admin' | 'user';
  permissions: {
    modules: string[];
    subcategories: string[];
  };
  showInWorkload?: boolean;
  isExternalUser?: boolean;
  hasPassword?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface RolesPermissions {
  roles: { [key: string]: string };
  modules: { [key: string]: string };
  subcategories: { [key: string]: string };
  defaultPermissions: {
    [key: string]: {
      modules: string[];
      subcategories: string[];
    };
  };
}

interface User {
  name: string;
  email: string;
  account: any;
}

interface TeamManagementProps {
  team: TeamMember[];
  setTeam: (team: TeamMember[]) => void;
  user: User | null;
  /**
   * Called after a team write that may have changed the SIGNED-IN user's own
   * permissions. App re-reads /api/user/permissions so the nav and every
   * module gate follow immediately (2026-08-21 audit).
   *
   * Without it, an admin who granted themselves a module kept the old nav
   * until a page reload: the team LIST was lifted back up, but the derived
   * permissions payload behind hasModuleAccess was not. usePermissions has
   * exported refetch() all along and nothing called it.
   */
  onOwnPermissionsMaybeChanged?: () => void;
}

const TeamManagement: React.FC<TeamManagementProps> = ({ team, setTeam, user, onOwnPermissionsMaybeChanged }) => {
  const { instance } = useMsal();
  // capacity dropped from the form at the 2026-08-13 walk (workload-tracker
  // residue); the model defaults new members to 40 server-side.
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    password: ''
  });
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [editingPassword, setEditingPassword] = useState('');
  const [rolesPermissions, setRolesPermissions] = useState<RolesPermissions | null>(null);
  const [userPermissions, setUserPermissions] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPermissions, setExpandedPermissions] = useState<{ [key: string]: boolean }>({});

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

  const getAuthHeaders = async () => {
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    try {
      const response = await instance.acquireTokenSilent({
        scopes: ['User.Read'],
        account: user.account
      });

      return {
        'Authorization': `Bearer ${response.accessToken}`,
        'x-user-email': user.email,
        'x-user-name': user.name
      };
    } catch (error) {
      console.error('Error getting auth headers:', error);
      throw error;
    }
  };

  useEffect(() => {
    if (user) {
      fetchRolesAndPermissions();
      fetchUserPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchRolesAndPermissions = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${backendUrl}/api/roles-permissions`, { headers });
      setRolesPermissions(response.data);
    } catch (error) {
      console.error('Error fetching roles and permissions:', error);
    }
  };

  // Case-insensitive: the team row and the signed-in identity can differ in
  // case, and a missed match here silently reinstates the stale-nav bug.
  const isSelf = (member: { email?: string }) =>
    !!member?.email && !!user?.email &&
    member.email.toLowerCase() === user.email.toLowerCase();

  const fetchUserPermissions = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${backendUrl}/api/user/permissions`, { headers });
      setUserPermissions(response.data);
    } catch (error) {
      console.error('Error fetching user permissions:', error);
    }
  };

  const handleAddMember = async () => {
    if (!newMember.name.trim() || !newMember.email.trim()) {
      setError('Name and email are required');
      return;
    }

    // Require password for external users
    if (isExternalEmail(newMember.email) && !newMember.password) {
      setError('Password is required for external users');
      return;
    }

    if (isExternalEmail(newMember.email) && newMember.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const memberData = isExternalEmail(newMember.email)
        ? newMember
        : { name: newMember.name, email: newMember.email, role: newMember.role };
      const response = await axios.post(`${backendUrl}/api/team`, memberData, { headers });
      setTeam(response.data);
      setNewMember({ name: '', email: '', role: 'user', password: '' });
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMember = async (member: TeamMember, newPassword?: string) => {
    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const memberData = newPassword
        ? { ...member, password: newPassword }
        : member;
      const response = await axios.post(`${backendUrl}/api/team`, memberData, { headers });
      setTeam(response.data);
      setEditingMember(null);
      setEditingPassword('');
      // Editing yourself changes what you are allowed to see. Refresh this
      // view's own copy and tell App to re-read its permissions.
      if (isSelf(member)) {
        fetchUserPermissions();
        onOwnPermissionsMaybeChanged?.();
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update team member');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (name: string) => {
    if (!window.confirm(`Are you sure you want to delete ${name}?`)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(`${backendUrl}/api/team/${encodeURIComponent(name)}`, { headers });
      setTeam(response.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete team member');
    } finally {
      setLoading(false);
    }
  };

  const toggleModuleAccess = (member: TeamMember, module: string) => {
    const updatedPermissions = { ...member.permissions || { modules: [], subcategories: [] } };
    if (updatedPermissions.modules.includes(module)) {
      updatedPermissions.modules = updatedPermissions.modules.filter(m => m !== module);
      // Also remove related subcategories
      updatedPermissions.subcategories = updatedPermissions.subcategories.filter(s => !s.startsWith(module));
    } else {
      updatedPermissions.modules.push(module);
      // Add default subcategories for this module
      if (rolesPermissions) {
        const moduleSubcategories = Object.values(rolesPermissions.subcategories)
          .filter(s => s.startsWith(module));
        moduleSubcategories.forEach(subcategory => {
          if (!updatedPermissions.subcategories.includes(subcategory)) {
            updatedPermissions.subcategories.push(subcategory);
          }
        });
      }
    }

    const updatedMember = { ...member, permissions: updatedPermissions };
    handleUpdateMember(updatedMember);
  };

  const toggleSubcategoryAccess = (member: TeamMember, subcategory: string) => {
    const updatedPermissions = { ...member.permissions || { modules: [], subcategories: [] } };
    if (updatedPermissions.subcategories.includes(subcategory)) {
      updatedPermissions.subcategories = updatedPermissions.subcategories.filter(s => s !== subcategory);
    } else {
      updatedPermissions.subcategories.push(subcategory);
    }

    const updatedMember = { ...member, permissions: updatedPermissions };
    handleUpdateMember(updatedMember);
  };

  const getModuleDisplayName = (moduleKey: string) => {
    // 2026-08-13 (legacy-archival): switch reflects the surviving MODULES
    // matrix in backend/models/teamMember.js. Archived module keys
    // (monday_data / content_production / content_approval) no longer appear
    // in rolesPermissions.modules, so they don't need entries here — any
    // stale key still on a team-doc record falls through to the raw
    // moduleKey default (ugly but visible, so admins notice).
    switch (moduleKey) {
      case 'team_settings': return 'Team Settings';
      case 'content_review': return 'Content Review';
      case 'video_editor': return 'Video Editor';
      case 'spec_writer': return 'Spec Writer';
      case 'strategy': return 'Strategy';
      default: return moduleKey;
    }
  };

  const getSubcategoryDisplayName = (subcategoryKey: string) => {
    const parts = subcategoryKey.split('.');
    const action = parts[1]?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return action || subcategoryKey;
  };

  const isExternalEmail = (email: string) => {
    return email && !email.toLowerCase().endsWith('@cellcolabs.com');
  };

  if (!user) {
    return <div>Please log in to access team settings.</div>;
  }

  if (!userPermissions) {
    return <div>Loading permissions...</div>;
  }

  if (!userPermissions.subcategoryAccess.canViewUsers) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>You don't have permission to view team settings.</p>
      </div>
    );
  }

  const canManageUsers = userPermissions.subcategoryAccess.canManageUsers;

  const togglePermissions = (memberName: string) => {
    setExpandedPermissions(prev => ({
      ...prev,
      [memberName]: !prev[memberName]
    }));
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2>Team Management</h2>
      
      {error && (
        <div style={{ 
          background: '#ffebee', 
          border: '1px solid #f44336', 
          padding: '10px', 
          borderRadius: '4px',
          marginBottom: '20px',
          color: '#c62828'
        }}>
          {error}
        </div>
      )}

      {canManageUsers && (
        <div style={{ 
          marginBottom: '30px', 
          padding: '24px', 
          border: '1px solid #e1e5e9', 
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ margin: '0 0 24px 0', color: '#333', fontSize: '18px', fontWeight: '600' }}>Add New Team Member</h3>
          
          <div className="add-member-form">
            {/* First Row - Name */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                Full Name *
              </label>
              <input
                type="text"
                placeholder="Enter full name"
                value={newMember.name}
                onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                style={{ 
                  width: '420px',
                  maxWidth: '100%',
                  padding: '14px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #d1d5db',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  (e.target as HTMLInputElement).style.borderColor = '#667eea';
                  (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  (e.target as HTMLInputElement).style.borderColor = '#d1d5db';
                  (e.target as HTMLInputElement).style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Second Row - Email */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                Email Address *
              </label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newMember.email}
                onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                style={{ 
                  width: '420px',
                  maxWidth: '100%',
                  padding: '14px 16px', 
                  borderRadius: '8px', 
                  border: '2px solid #d1d5db',
                  fontSize: '15px',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                  backgroundColor: '#ffffff',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => {
                  (e.target as HTMLInputElement).style.borderColor = '#667eea';
                  (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  (e.target as HTMLInputElement).style.borderColor = '#d1d5db';
                  (e.target as HTMLInputElement).style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Third Row - Role. (Capacity input removed at the 2026-08-13
                walk — it was Monday workload-tracker residue with no live
                consumer; the DB field stays as idle data, model defaults 40.) */}
            <div style={{ display: 'grid', gridTemplateColumns: '180px', gap: '20px', marginBottom: '24px', width: '420px', maxWidth: '100%' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  Role
                </label>
                <select
                  value={newMember.role}
                  onChange={(e) => setNewMember({ ...newMember, role: e.target.value as 'admin' | 'user' })}
                  style={{ 
                    width: '100%',
                    padding: '14px 16px', 
                    borderRadius: '8px', 
                    border: '2px solid #d1d5db',
                    fontSize: '15px',
                    backgroundColor: '#ffffff',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    (e.target as HTMLSelectElement).style.borderColor = '#667eea';
                    (e.target as HTMLSelectElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLSelectElement).style.borderColor = '#d1d5db';
                    (e.target as HTMLSelectElement).style.boxShadow = 'none';
                  }}
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {/* Password Field - Only for external users */}
            {isExternalEmail(newMember.email) && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                  Password * <span style={{ fontWeight: 'normal', color: '#6b7280', fontSize: '12px' }}>(for external user login)</span>
                </label>
                <input
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={newMember.password}
                  onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
                  style={{
                    width: '420px',
                    maxWidth: '100%',
                    padding: '14px 16px',
                    borderRadius: '8px',
                    border: '2px solid #d1d5db',
                    fontSize: '15px',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    backgroundColor: '#ffffff',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = '#667eea';
                    (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    (e.target as HTMLInputElement).style.borderColor = '#d1d5db';
                    (e.target as HTMLInputElement).style.boxShadow = 'none';
                  }}
                />
                <p style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
                  This user will sign in with email and password instead of Microsoft login.
                </p>
              </div>
            )}

            {/* Fourth Row - Add Button */}
            <div style={{ textAlign: 'left' }}>
              <button
                onClick={handleAddMember}
                disabled={loading}
                style={{ 
                  padding: '14px 32px', 
                  backgroundColor: loading ? '#9ca3af' : '#667eea', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '15px',
                  fontWeight: '600',
                  transition: 'all 0.2s ease',
                  boxShadow: loading ? 'none' : '0 4px 6px rgba(102, 126, 234, 0.25)',
                  whiteSpace: 'nowrap',
                  height: '52px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    (e.target as HTMLElement).style.backgroundColor = '#5a67d8';
                    (e.target as HTMLElement).style.transform = 'translateY(-1px)';
                    (e.target as HTMLElement).style.boxShadow = '0 6px 8px rgba(102, 126, 234, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    (e.target as HTMLElement).style.backgroundColor = '#667eea';
                    (e.target as HTMLElement).style.transform = 'translateY(0)';
                    (e.target as HTMLElement).style.boxShadow = '0 4px 6px rgba(102, 126, 234, 0.25)';
                  }
                }}
              >
                {loading ? (
                  <>
                    <span style={{ marginRight: '8px' }}>⏳</span>
                    Adding...
                  </>
                ) : (
                  <>
                    <span style={{ marginRight: '8px' }}>👤</span>
                    Add Member
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div>
        <h3>Team Members</h3>
        {team.map((member) => (
          <div key={member.name} style={{ 
            marginBottom: '20px', 
            padding: '20px', 
            border: '1px solid #ddd', 
            borderRadius: '8px',
            backgroundColor: '#ffffff'
          }}>
            <div className="member-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <div>
                <h4 style={{ margin: '0 0 5px 0' }}>{member.name}</h4>
                {/* Capacity + roster toggle removed at the 2026-08-13 walk —
                    Monday workload-tracker residue with no live consumer.
                    showInWorkload/capacity stay on the DB record as idle data. */}
                <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
                  {member.email || 'No email'} • {(member.role || 'user').toUpperCase()}
                </p>
              </div>
              {canManageUsers && (
                <div>
                  <button 
                    onClick={() => setEditingMember(member)}
                    style={{ 
                      padding: '6px 12px', 
                      backgroundColor: '#2196f3', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px',
                      marginRight: '10px',
                      cursor: 'pointer'
                    }}
                  >
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDeleteMember(member.name)}
                    style={{ 
                      padding: '6px 12px', 
                      backgroundColor: '#f44336', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>

            {/* Permissions Section */}
            <div>
              <button
                onClick={() => togglePermissions(member.name)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#667eea',
                  padding: '8px 0',
                  marginBottom: '10px'
                }}
              >
                <span style={{ 
                  transform: expandedPermissions[member.name] ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}>
                  ▶
                </span>
                Module & Permission Settings
              </button>
              
              {expandedPermissions[member.name] && (
                <div style={{ 
                  paddingLeft: '20px',
                  borderLeft: '3px solid #f0f0f0',
                  marginLeft: '10px'
                }}>
                  {/* Permissions — grouped per module (walk station 2):
                      each module is a group with its features indented
                      beneath it, so "which feature belongs to which module"
                      is visible at a glance. Admin-role members show a
                      badge instead of checkboxes: hasModule/SubcategoryAccess
                      auto-grants for admins, so a checkbox matrix would be
                      misleading decoration. */}
                  {(member.role === 'admin') ? (
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      background: '#e8f0fe',
                      color: '#123250',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: 500
                    }}>
                      🛡 Admin — full access to all modules and features
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {rolesPermissions && Object.values(rolesPermissions.modules).map(moduleValue => {
                        const hasModule = member.permissions?.modules?.includes(moduleValue) || false;
                        const moduleSubcats = Object.values(rolesPermissions.subcategories)
                          .filter(s => s.startsWith(moduleValue + '.'));
                        return (
                          <div key={moduleValue} style={{
                            border: '1px solid #eee',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            background: hasModule ? '#fbfcfe' : '#fafafa'
                          }}>
                            <label style={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: canManageUsers ? 'pointer' : 'default',
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#333'
                            }}>
                              <input
                                type="checkbox"
                                checked={hasModule}
                                onChange={() => canManageUsers && toggleModuleAccess(member, moduleValue)}
                                disabled={!canManageUsers}
                                style={{ marginRight: '8px' }}
                              />
                              {getModuleDisplayName(moduleValue)}
                            </label>
                            {hasModule && moduleSubcats.length > 0 && (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
                                gap: '6px',
                                marginTop: '8px',
                                marginLeft: '24px'
                              }}>
                                {moduleSubcats.map(subValue => (
                                  <label key={subValue} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '13px',
                                    cursor: canManageUsers ? 'pointer' : 'default',
                                    padding: '3px 6px',
                                    color: '#555'
                                  }}>
                                    <input
                                      type="checkbox"
                                      checked={member.permissions?.subcategories?.includes(subValue) || false}
                                      onChange={() => canManageUsers && toggleSubcategoryAccess(member, subValue)}
                                      disabled={!canManageUsers}
                                      style={{ marginRight: '6px' }}
                                    />
                                    {getSubcategoryDisplayName(subValue)}
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Edit Member Modal */}
      {editingMember && canManageUsers && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '8px',
            width: '500px',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <h3>Edit Team Member</h3>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Name:</label>
              <input
                type="text"
                value={editingMember.name}
                onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Email:</label>
              <input
                type="email"
                value={editingMember.email}
                onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              />
            </div>
            {/* Capacity + roster toggle removed at the 2026-08-13 walk —
                no live consumer post-archival; DB fields stay as idle data. */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px' }}>Role:</label>
              <select
                value={editingMember.role}
                onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value as 'admin' | 'user' })}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {/* Password Reset - Only for external users */}
            {editingMember.isExternalUser && (
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                  Reset Password
                </label>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                  {editingMember.hasPassword
                    ? 'Leave blank to keep current password, or enter a new password to reset it.'
                    : 'This external user has no password set. Enter a password to enable login.'}
                </p>
                <input
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={editingPassword}
                  onChange={(e) => setEditingPassword(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setEditingMember(null);
                  setEditingPassword('');
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateMember(editingMember, editingPassword || undefined)}
                disabled={loading || (editingPassword.length > 0 && editingPassword.length < 6)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (loading || (editingPassword.length > 0 && editingPassword.length < 6)) ? '#9ca3af' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (loading || (editingPassword.length > 0 && editingPassword.length < 6)) ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;